import { useEffect, useRef } from 'react';
import { useWallet } from '@terra-money/wallet-provider';

/**
 * Keeps the connected address in step with the account selected in the
 * extension.
 *
 * wallet-provider 4.x never notices that switch on its own. It registers no
 * focus, visibility or polling listener, and createFixedExtension drops an
 * unsolicited `onConnect` push because its resolver set is only populated
 * while a connect() call is in flight. LegacyExtensionConnector pushes new
 * state exclusively from recheckStates(), whose only entry points are the
 * initial open(), requestApproval() and refetchStates(). So without this the
 * address goes stale until the page is reloaded.
 *
 * refetchStates() re-asks the extension which account is active. Firing it
 * when the tab regains focus matches how the switch actually happens: the user
 * leaves the tab for the extension, picks another account, and comes back.
 *
 * Cheap and safe to call: it is a no-op unless an extension is connected, and
 * recheckStates() bails out while a transaction is in progress, so it cannot
 * interrupt a signing flow.
 */
export function useWalletAddressSync() {
    // Destructuring straight off useWallet() would throw if this ever ended up
    // outside WalletProvider; the hook should degrade to doing nothing instead.
    const wallet = useWallet();
    const refetchStates = wallet?.refetchStates;
    const lastRunRef = useRef(0);

    useEffect(() => {
        if (typeof refetchStates !== 'function') return undefined;

        const sync = () => {
            if (document.visibilityState !== 'visible') return;
            // A tab switch fires focus and visibilitychange together; one round
            // trip to the extension per switch is enough.
            const now = Date.now();
            if (now - lastRunRef.current < 1000) return;
            lastRunRef.current = now;
            refetchStates();
        };

        window.addEventListener('focus', sync);
        document.addEventListener('visibilitychange', sync);
        return () => {
            window.removeEventListener('focus', sync);
            document.removeEventListener('visibilitychange', sync);
        };
    }, [refetchStates]);
}
